import { useEffect, useState } from "react";
import { isValid, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";

import { MAX_COST_INDEX, MEDIAN_COST_INDEX, MIN_COST_INDEX } from "./constants";
import PriceAdvisorRepairPartsLink from "./PriceAdvisorRepairPartsLink";
import PriceLine from "./PriceLine";
import PriceTitle from "./PriceTitle";
import RoomForImprovementBanner from "./RoomForImprovementBanner";
import {
  PartRecommendation,
  RepairRecommendation,
  ServiceCenter,
} from "./types";
import {
  buildRepairPartsFilter,
  getVendorLocation,
  getVendorName,
} from "./utils";

interface Props {
  partRecommendation: PartRecommendation;
  recommendation: RepairRecommendation;
  serviceCentersByID: Record<string, ServiceCenter>;
  serviceCenterCurrent: string;
  assetFilters: string;
  workOrderID: string;
  workOrderDate: string;
  consideredHistoryDays: number;
  isStreaming: boolean;
  hasAssetData: boolean;
}

const PartRecommendationSection = ({
  partRecommendation,
  recommendation,
  serviceCentersByID,
  serviceCenterCurrent,
  assetFilters,
  workOrderID,
  workOrderDate,
  consideredHistoryDays,
  isStreaming,
  hasAssetData,
}: Props) => {
  const { t } = useTranslation("duck");
  const [isExpanded, setIsExpanded] = useState(
    partRecommendation.room_for_improvement
  );

  useEffect(() => {
    setIsExpanded(partRecommendation.room_for_improvement);
  }, [partRecommendation.room_for_improvement]);

  if (
    !partRecommendation.service_center_alternatives &&
    !partRecommendation.web_alternatives
  ) {
    return null;
  }

  const hasExpandableContent =
    partRecommendation.service_center_alternatives.length > 0 ||
    partRecommendation.web_alternatives.length > 0;

  const minimum = Math.min(
    ...partRecommendation.service_center_alternatives.map(
      (alternative) =>
        alternative.cost_min_median_max?.[MIN_COST_INDEX] ?? Infinity
    ),
    partRecommendation.cost_parts ?? Infinity,
    ...partRecommendation.web_alternatives.map(
      (alternative) => alternative.price ?? Infinity
    )
  );

  const maximum = Math.max(
    ...partRecommendation.service_center_alternatives.map(
      (alternative) =>
        alternative.cost_min_median_max?.[MAX_COST_INDEX] ?? -Infinity
    ),
    partRecommendation.cost_parts ?? -Infinity,
    ...partRecommendation.web_alternatives.map(
      (alternative) => alternative.price ?? -Infinity
    ),
    0
  );

  const parsedWorkOrderDate = workOrderDate
    ? parseISO(workOrderDate)
    : undefined;
  const canBuildLinks =
    hasAssetData &&
    Boolean(workOrderDate) &&
    Boolean(workOrderID) &&
    Number.isFinite(consideredHistoryDays) &&
    Boolean(parsedWorkOrderDate) &&
    isValid(parsedWorkOrderDate);

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex space-x-2">
            <span className="font-semibold ">
              {partRecommendation.part_description}
            </span>
            <span className="text-sm">
              {partRecommendation.vmrs_id || partRecommendation.part_id}
            </span>
            <span className="text-sm">
              {partRecommendation.part_quantity &&
                partRecommendation.part_quantity > 1 &&
                `(${partRecommendation.part_quantity}x)`}
            </span>
          </div>
        </TableCell>
        <TableCell>
          <PriceTitle
            minimum={minimum}
            maximum={maximum}
            quoted={partRecommendation.cost_parts}
            currencyCode={recommendation.currency_code}
            aiEstimate={partRecommendation.cost_parts_estimate}
            showCurrencyForEstimate={true}
            hasExpandableContent={hasExpandableContent}
            onToggleExpand={() => setIsExpanded(!isExpanded)}
            isExpanded={isExpanded}
          />
        </TableCell>
      </TableRow>
      {partRecommendation.room_for_improvement && (
        <TableRow
          sx={{
            display: isExpanded ? "table-row" : "none",
            opacity: isExpanded ? 1 : 0,
            transition: "opacity 300ms",
          }}
        >
          <TableCell colSpan={2} sx={{ p: 0, borderBottom: "none" }}>
            <RoomForImprovementBanner />
          </TableCell>
        </TableRow>
      )}
      {partRecommendation.service_center_alternatives.map(
        (alternative, index) => (
          <TableRow
            key={`${alternative.service_center_id || "aggregated"}-${index}`}
            sx={{
              display: isExpanded ? "table-row" : "none",
              opacity: isExpanded ? 1 : 0,
              transition: "opacity 300ms",
            }}
          >
            <TableCell>
              <div className="ml-4">
                <div className="flex space-x-2 items-center">
                  <span className="font-semibold text-xs">
                    {getVendorName(
                      serviceCentersByID,
                      t,
                      alternative.service_center_id,
                      isStreaming
                    )}
                  </span>
                  {canBuildLinks && (
                    <PriceAdvisorRepairPartsLink
                      repairPartsFilter={buildRepairPartsFilter(
                        partRecommendation.vmrs_id,
                        recommendation.repair_id,
                        partRecommendation.part_quantity,
                        workOrderDate,
                        workOrderID,
                        consideredHistoryDays,
                        partRecommendation.min_threshold_cost_parts,
                        partRecommendation.max_threshold_cost_parts,
                        alternative.service_center_id
                          ? serviceCentersByID[alternative.service_center_id]
                              ?.brand_name
                          : undefined
                      )}
                      repairPartsCount={alternative.repairs_count}
                      assetFilter={assetFilters}
                    />
                  )}
                </div>
                <div className="text-xs text-neutral-500">
                  {getVendorLocation(
                    serviceCentersByID,
                    serviceCenterCurrent,
                    t,
                    alternative.service_center_id
                  )}
                </div>
              </div>
            </TableCell>
            <TableCell>
              <PriceLine
                quoted={partRecommendation.cost_parts}
                minimum={minimum}
                maximum={maximum}
                current={alternative.cost_min_median_max?.[MEDIAN_COST_INDEX]!}
                currentMin={alternative.cost_min_median_max?.[MIN_COST_INDEX]}
                currentMax={alternative.cost_min_median_max?.[MAX_COST_INDEX]}
                currencyCode={recommendation.currency_code}
              />
            </TableCell>
          </TableRow>
        )
      )}
      {partRecommendation.web_alternatives.length > 0 && (
        <>
          <TableRow
            sx={{
              display: isExpanded ? "table-row" : "none",
              opacity: isExpanded ? 1 : 0,
              transition: "opacity 300ms",
            }}
          >
            <TableCell colSpan={2}>
              <div className="text-xs text-semibold ml-4">
                {t("retail_alternatives")}
              </div>
            </TableCell>
          </TableRow>
          {partRecommendation.web_alternatives.map((alternative, index) => (
            <TableRow
              key={`${alternative.link || "web"}-${index}`}
              sx={{
                display: isExpanded ? "table-row" : "none",
                opacity: isExpanded ? 1 : 0,
                transition: "opacity 300ms",
              }}
            >
              <TableCell>
                <div className="ml-4">
                  <div>
                    <a
                      className="underline text-semibold"
                      href={alternative.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {alternative.vendor}
                    </a>
                  </div>
                  <div className="text-sm text-neutral-500">
                    {alternative.smtp_part_description}{" "}
                    {alternative.smtp_part_num}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <PriceLine
                  quoted={partRecommendation.cost_parts}
                  minimum={minimum}
                  maximum={maximum}
                  current={alternative.price}
                  currencyCode={recommendation.currency_code}
                />
              </TableCell>
            </TableRow>
          ))}
        </>
      )}
    </>
  );
};

export default PartRecommendationSection;
